import type React from "react";
import { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  CircularProgress,
  Card,
  CardContent,
  useMediaQuery,
  useTheme,
  Grid,
} from "@mui/material";
import { Print as PrintIcon } from "@mui/icons-material";
import apiService from "../../service/Api";
import { useSelector } from "react-redux";
import type { RootState } from "../../store/Index";
import { toast } from "react-toastify";
import muzukuruBlack from "../../assets/muzukurublack.png";
import "../../print.css";

// Import components for date picker and Dayjs
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { type Dayjs } from "dayjs";
import CustomTextField from "../../components/common/Textfield";

interface StreamEvent {
  id: string;
  day: string;
  date: string;
  event: string;
  branch: string;
  producer: string;
  location: string;
  shootStartTime: string;
}

interface ApiResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: {
    id: number;
    booking: {
      id: number;
      deceased: {
        salutation: string;
        first_name: string;
        surname: string;
      }[];
    };
    service: {
      id: number;
      venue: {
        address_one: string;
        address_two: string;
        suburb: string;
        city: string;
      };
      date_of_service: string;
      start_time: string;
    };
    title: string;
    start: string;
    date: string;
    address_one: string;
    address_two: string;
    suburb: string;
    city: string;
  }[];
}

const TenDayForecast: React.FC = () => {
  const [startDate, setStartDate] = useState<Dayjs | null>(dayjs());
  const [endDate, setEndDate] = useState<Dayjs | null>(null);
  const reduxTheme = useSelector((state: RootState) => state.theme);
  const [streams, setStreams] = useState<StreamEvent[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const fetchStreams = useCallback(async () => {
    // Check for null values
    if (!startDate || !endDate) return;

    setIsLoading(true);
    try {
      const params = new URLSearchParams({
        start_date: startDate.format("YYYY-MM-DD"),
        end_date: endDate.format("YYYY-MM-DD"),
      });

      const response = await apiService.get<ApiResponse>(
        `/api/streams/stream/ten-day-forecast/?${params.toString()}`
      );

      const mappedStreams = response.data.results.map((stream) => {
        const deceased = stream.booking?.deceased?.[0];
        const eventTitle = deceased
          ? `${deceased.salutation} ${deceased.first_name} ${deceased.surname}`
          : stream.title || "Unnamed Stream";

        const location = stream.service?.venue
          ? `${stream.service.venue.address_one || ""}, ${
              stream.service.venue.address_two || ""
            }, ${stream.service.venue.suburb || ""}, ${
              stream.service.venue.city || ""
            }`
              .replace(/, ,/g, ",")
              .replace(/(^,|,$)/g, "")
              .trim()
          : `${stream.address_one || ""}, ${stream.address_two || ""}, ${
              stream.suburb || ""
            }, ${stream.city || ""}`
              .replace(/, ,/g, ",")
              .replace(/(^,|,$)/g, "")
              .trim();

        const shootStartTime = stream.service?.start_time
          ? dayjs(`1970-01-01T${stream.service.start_time}`).format("HHmm") +
            "hrs"
          : stream.start;

        const dateObject = dayjs(
          stream.service?.date_of_service || stream.date
        );

        return {
          id: String(stream.id),
          day: dateObject.format("dddd"),
          date: dateObject.format("MMM DD YYYY"),
          event: eventTitle,
          branch: "N/A",
          producer: "N/A",
          location: location,
          shootStartTime: shootStartTime,
        };
      });

      setStreams(mappedStreams);
      toast.success("Confirmed Streams fetched successfully", {
        position: "top-right",
        autoClose: 5000,
        theme: "light",
      });
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message || "Failed to fetch streams";
      toast.error(errorMessage, {
        position: "top-right",
        autoClose: 5000,
        theme: "light",
      });
      setStreams([]);
    } finally {
      setIsLoading(false);
    }
  }, [startDate, endDate]);

  useEffect(() => {
    if (startDate) {
      const tenDaysLater = startDate.add(9, "day");
      setEndDate(tenDaysLater);
    }
  }, [startDate]);

  useEffect(() => {
    fetchStreams();
  }, [fetchStreams]);

  // Update handler to accept Dayjs | null
  const handleStartDateChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = event.target.value;
    setStartDate(value ? dayjs(value) : null);
  };

  const handlePrint = () => {
    window.print();
  };

  const formatDateRange = () => {
    if (startDate && endDate) {
      return `${startDate.format("DD MMM YYYY")} - ${endDate.format(
        "DD MMM YYYY"
      )}`;
    }
    return "Select dates";
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box
        sx={{
          maxWidth: { xs: "100%", md: 1400 },
          margin: "0 auto",
          px: { xs: 1, md: 0 },
        }}
      >
        {/* Header: desktop unchanged; mobile will hide date & print */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            justifyContent: { xs: "center", md: "space-between" },
            alignItems: { xs: "center", md: "center" },
            mb: 3,
            gap: 2,
            textAlign: { xs: "center", md: "left" },
          }}
        >
          {/* Logo (desktop only) */}
          <Box sx={{ display: { xs: "none", md: "block" } }}>
            <img
              src={muzukuruBlack || "/placeholder.svg"}
              alt="Logo"
              style={{
                width: "160px",
                cursor: "pointer",
                borderRadius: "50%",
              }}
            />
          </Box>

          {/* Title + Start Date (start date visible only on desktop) */}
          <Box sx={{ textAlign: "center" }}>
            <Typography
              variant="h5"
              component="h2"
              sx={{ color: "text.secondary", mb: 1 }}
            >
              Producer's 10 Day Forecast
            </Typography>
            <Box sx={{ mt: 1 }}>
              {/* HIDE start date on mobile */}
              <Box sx={{ display: { xs: "none", md: "block" } }}>
                <CustomTextField
                  label="Start Date"
                  type="date"
                  value={startDate ? startDate.format("YYYY-MM-DD") : ""}
                  onChange={handleStartDateChange}
                  sx={{ width: { xs: "100%", md: "auto" } }}
                />
              </Box>
            </Box>
          </Box>

          {/* Date range + print (desktop only) */}
          <Box
            sx={{
              display: { xs: "none", md: "flex" },
              flexDirection: { xs: "column", md: "row" },
              alignItems: "center",
              gap: 2,
              textAlign: { xs: "center", md: "left" },
            }}
          >
            <Typography variant="body1" sx={{ color: "text.secondary" }}>
              {formatDateRange()}
            </Typography>
            <IconButton className="print-header" onClick={handlePrint}>
              <PrintIcon />
            </IconButton>
          </Box>
        </Box>

        {/* Desktop table (unchanged) */}
        {!isMobile && (
          <TableContainer component={Paper} sx={{ boxShadow: 0 }}>
            <Table>
              <TableHead>
                <TableRow
                  sx={{ backgroundColor: reduxTheme.secondary_colors[500] }}
                >
                  <TableCell sx={{ fontWeight: "bold" }}>Day</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Date</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Event</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Producer</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Location</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>
                    Shoot Start Time
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell
                      colSpan={7}
                      sx={{
                        textAlign: "center",
                        py: 4,
                        color: "text.secondary",
                      }}
                    >
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : streams.length > 0 ? (
                  streams.map((stream) => (
                    <TableRow
                      key={stream.id}
                      sx={{ "&:hover": { bgcolor: "grey.50" } }}
                    >
                      <TableCell>{stream.day}</TableCell>
                      <TableCell>{stream.date}</TableCell>
                      <TableCell>{stream.event}</TableCell>
                      <TableCell>{stream.producer}</TableCell>
                      <TableCell sx={{ width: 300 }}>
                        {stream.location}
                      </TableCell>
                      <TableCell sx={{ fontWeight: "bold" }}>
                        {stream.shootStartTime}
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={7}
                      sx={{
                        textAlign: "center",
                        py: 4,
                        color: "text.secondary",
                      }}
                    >
                      No confirmed streams found for the selected period
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {isMobile && (
          <Box>
            {isLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                <CircularProgress />
              </Box>
            ) : streams.length > 0 ? (
              <Grid container spacing={2}>
                {streams.map((stream) => (
                  <Grid size={{ xs: 12 }} key={stream.id}>
                    <Card
                      sx={{
                        borderRadius: 3,
                        overflow: "hidden",
                        boxShadow: "0px 6px 18px rgba(0,0,0,0.08)",
                      }}
                    >
                      {/* header bar */}
                      <Box
                        sx={{
                          backgroundColor: reduxTheme.secondary_colors[400],
                          color: reduxTheme.primary_colors[900],
                          px: 2,
                          py: 1,
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <Typography
                          sx={{ fontWeight: 700, fontSize: "0.9rem" }}
                        >
                          {stream.date}
                        </Typography>
                        <Typography
                          sx={{ fontWeight: 700, fontSize: "0.9rem" }}
                        >
                          {stream.shootStartTime}
                        </Typography>
                      </Box>

                      <CardContent sx={{ pt: 2, pb: 2 }}>
                        <Box sx={{ mb: 1 }}>
                          <Typography
                            sx={{ fontSize: "1rem", fontWeight: 700 }}
                            noWrap
                            title={stream.event}
                          >
                            {stream.event}
                          </Typography>
                        </Box>

                        <Box sx={{ mb: 1 }}>
                          <Typography
                            sx={{
                              fontSize: "0.875rem",
                              color: "text.secondary",
                              lineHeight: 1.3,
                            }}
                            noWrap
                            title={stream.location}
                          >
                            {stream.location}
                          </Typography>
                        </Box>

                        <Box
                          sx={{
                            display: "inline-block",
                            backgroundColor: reduxTheme.primary_colors[800],
                            color: reduxTheme.secondary_colors[300],
                            px: 1.5,
                            py: 0.5,
                            borderRadius: 2,
                            fontSize: "0.75rem",
                            fontWeight: 600,
                          }}
                        >
                          {stream.branch ?? "Stream"}
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Box sx={{ textAlign: "center", py: 4 }}>
                <Typography sx={{ color: "text.secondary" }}>
                  No confirmed streams found for the selected period
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </Box>
    </LocalizationProvider>
  );
};

export default TenDayForecast;
